import { Breadcrumb, Flex, GridAlpha } from '@applyboard/crystal-ui';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { UniqueApplicationsListProvider } from '../context/UniqueApplicationsListProvider';
import { ApplicationDetailsTabs } from '../components/ApplicationDetailsTabs/ApplicationDetailsTabs';
import React from 'react';

import {
  ProgramDetails,
  ApplicationDetailsHeading,
  StatusAndCitizenship,
  ContactInformation,
  ApplicantInformation,
  EducationHistory,
  LanguageProficiency,
  OtherRequiredDocuments,
  ApplicationProcessing,
  CardControlActions,
  DecisionLetters,
  UploadAdditionalDocuments,
  EvaluationTab,
  // processing
  AdditionalDocumentNotice,
  DeferralRequestNotice,
  UniqueApplicationsNotice,
  UniqueApplicationsListTable,
} from './../components';
import { ApplicationAssignment } from '../components/Cards/ApplicationAssignment';
import { AlternateProgramInformation } from '../components/Cards/AlternateProgramInformation';
import { BreadcrumbItem } from '~/utils';
import { ApplicationTimeline } from '../components/Cards/ApplicationTimeline';
import { useGetCustomizedDocumentTypes } from '../data/useGetCustomizedDocumentTypes';
import { useSchoolCustomization } from '~/context/SchoolCustomizationsProvider';

type MobileLayoutProps = {
  breadcrumbItems: BreadcrumbItem[];
};

export function MobileLayout(props: Readonly<MobileLayoutProps>) {
  const { application } = useAppProcessing();
  const { customizedDocumentTypes } = useGetCustomizedDocumentTypes();
  const { schoolCustomizations } = useSchoolCustomization();
  const isSkipAlternateProgram = schoolCustomizations?.schoolCustomization?.isSkipAlternateProgram ?? false;
  const transcriptAiEnabled = (() => {
    const docTypes = (
      customizedDocumentTypes?.attributes as
        | {
            educationHistoryDocumentTypes?: Array<{
              type?: string;
              AiProcessDocumentEnabled?: boolean;
              aiProcessDocumentEnabled?: boolean;
            }>;
          }
        | undefined
    )?.educationHistoryDocumentTypes;
    const transcriptDoc = Array.isArray(docTypes) ? docTypes.find((d) => d.type === 'TRANSCRIPT') : undefined;
    if (!transcriptDoc) return true;
    return (
      transcriptDoc.AiProcessDocumentEnabled ??
      (transcriptDoc as { aiProcessDocumentEnabled?: boolean }).aiProcessDocumentEnabled ??
      true
    );
  })();
  const shouldRenderAIMLFeature =
    Boolean(application.statusAndCitizenship.documents[0]) &&
    Boolean(application.statusAndCitizenship.documents[0].documentReviewSuggestion);

  const passportId =
    application.statusAndCitizenship.passportId !== undefined
      ? application.statusAndCitizenship.passportId
      : 'NOT_AVAILABLE';
  const currentApplicationId = application.id;

  const listRef = React.useRef<HTMLDivElement>(null);

  function scrollToApplicationsTable() {
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function applicationDetailsLayout() {
    return (
      <GridAlpha columns="minmax(0, 1fr)">
        <Flex gap={8} direction="column">
          <ApplicantInformation />
          <ContactInformation />
          {!shouldRenderAIMLFeature && <StatusAndCitizenship />}
          <EducationHistory />
          <LanguageProficiency />
          <OtherRequiredDocuments />
          <ApplicationTimeline />
          {!isSkipAlternateProgram && <AlternateProgramInformation />}
          <ApplicationAssignment />
          <div ref={listRef}>{passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsListTable />}</div>
        </Flex>
      </GridAlpha>
    );
  }
  return (
    <div aria-label="mobile" style={{ marginBottom: '10px' }}>
      <UniqueApplicationsListProvider passportId={passportId} currentApplicationId={currentApplicationId}>
        <Flex pt={10} pb={2} px={8} gap={6} direction="column">
          <Breadcrumb items={props.breadcrumbItems} />
        </Flex>

        <Flex px={8} gap={6} direction="column">
          <Flex justify="between" align="stretch" direction="column">
            <ApplicationDetailsHeading />
            <ApplicationProcessing />
          </Flex>
          <DeferralRequestNotice />
          <AdditionalDocumentNotice />
          <ProgramDetails lineSplit={true} />
          {passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsNotice onViewTableClick={scrollToApplicationsTable} />}
          <CardControlActions />
          <ApplicationDetailsTabs
            submittedApplicationDetails={applicationDetailsLayout()}
            applicationDetails={
              <Flex gap={8} align="stretch" direction="column">
                <DecisionLetters />
                <UploadAdditionalDocuments />
              </Flex>
            }
            evaluationDetails={<EvaluationTab />}
            showEvaluationTab={transcriptAiEnabled}
          />
        </Flex>
      </UniqueApplicationsListProvider>
    </div>
  );
}
